import { useCreateDataTableOptions } from "@/components/data-table/hooks/useCreateDataTableOptions";
import { useDynamicQueryParams } from "@/hooks/useDynamicQueryParams";
import { Filters, mergeFilters } from "@/lib/filters";
import { AuditLogsService } from "@/modules/auditLogs/service";
import { ReadAuditLog } from "@/types/auditLog";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { useMemo } from "react";

export const useAuditLogsQuery = ({
  name = "audits",
  enabled = true,
  filters: filtersFromProps,
  defaultSorting = { id: "changed_at", desc: true },
  keepPreviousData: keepPreviousDataOption = true,
}: {
  name?: string;
  enabled?: boolean;
  filters?: Filters<ReadAuditLog>;
  defaultSorting?: { id: string; desc: boolean };
  keepPreviousData?: boolean;
} = {}) => {
  const options = useCreateDataTableOptions<ReadAuditLog>(name);
  const filters = useMemo(
    () => mergeFilters(options.filters, filtersFromProps),
    [filtersFromProps, options.filters],
  );

  // Merge filters into options for queryKey to include filtersFromProps
  const optionsWithMergedFilters = useMemo(
    () => ({
      ...options,
      filters,
    }),
    [options, filters],
  );

  const queryKey = useDynamicQueryParams(name, optionsWithMergedFilters);
  const sorting = useMemo(
    () => options.sorting[0] || defaultSorting,
    [options.sorting, defaultSorting],
  );
  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) =>
      AuditLogsService.getLogs({
        lastItem: pageParam,
        filters,
        sorting,
      }),
    initialPageParam: undefined,
    getNextPageParam: ({ last_item }) => last_item,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousDataOption ? keepPreviousData : undefined,
    enabled,
  });

  return {
    query,
    options,
    queryKey,
  };
};
